import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage, defineMessages } from 'react-intl'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import history from 'src/history'
import { OVERLAY_HELPERS } from 'src/constants/navigation'
import { useFormatMessage } from 'src/providers/intl'
import { selectShouldRenderSmsOptInFlow } from 'src/store/selectors/display'
import {
  setClientIsSubscribedToHome,
  setClientIsSubscribedToBuy,
  setClientOptedIntoSms,
  resetClientStatus
} from 'src/actions/client'
import {
  selectClientEmail,
  selectClientMobile,
  selectClientIsBuyer,
  selectClientIsSubscribedToSms,
  selectClientIsSubscribedToHome,
  selectClientIsSubscribedToBuy,
  selectClientHasSubscribedToSms,
  selectClientIsTwilioBlacklisted
} from 'src/store/selectors/client'
import { If, Unless, Text, Button, CheckBox, Format } from 'src/Components'
import { IconPencil } from 'src/Components/shared/Icon'
import { TEXT_UPDATE_PAGES } from 'src/Components/shared/TextUpdatesPanel'

import panelStyles from 'src/Components/shared/Panel/Panel.module.scss'
import styles from './PreferencesPanel.module.scss'

import EnvelopeHB from './assets/EnvelopeHB.svg'
import MobileHB from './assets/MobileHB.svg'

const isExplainerImplemented = false
const isNotificationsImplemented = false
const notificationsActive = false
const handleNotificationsToggle = () => console.log('not implemented')
const handleShowExplainer = () => console.log('not implemented')

const PreferencesPanel = () => {
  const dispatch = useDispatch()
  const clientEmail = useSelector(selectClientEmail)
  const clientMobile = useSelector(selectClientMobile)
  const renderSmsOptIn = useSelector(selectShouldRenderSmsOptInFlow)

  const hasSubbed = useSelector(selectClientHasSubscribedToSms)
  const isBuyer = useSelector(selectClientIsBuyer)
  const outViaTwilio = useSelector(selectClientIsTwilioBlacklisted)

  const homeReportsActive = useSelector(selectClientIsSubscribedToHome)
  const buyerReportsActive = useSelector(selectClientIsSubscribedToBuy)
  const homeReportsSmsActive = useSelector(selectClientIsSubscribedToSms)

  const navToEmailUpdates = () =>
    history.push(OVERLAY_HELPERS.emailUpdates.buildPath())
  const navToTextUpdates = key => {
    dispatch(resetClientStatus())
    history.push(OVERLAY_HELPERS.textUpdates.buildPath(key))
  }

  const handleHomeReportsToggle = e =>
    dispatch(setClientIsSubscribedToHome({ setTo: e.target.checked }))
  const handleBuyerReportsToggle = e =>
    dispatch(setClientIsSubscribedToBuy({ setTo: e.target.checked }))
  const handleHomeReportsSMSToggle = e => {
    if (outViaTwilio) {
      e.target.checked = homeReportsSmsActive
      navToTextUpdates(TEXT_UPDATE_PAGES.TWILIO)
    } else {
      dispatch(setClientOptedIntoSms({ setTo: e.target.checked }))
    }
  }

  return (
    <div className={classnames(panelStyles.container, styles.container)}>
      <div className={styles.header}>
        <Text size='xl'>
          <FormattedMessage
            id='Preferences.panel.title'
            defaultMessage='How should we send updates?'
          />
        </Text>
        <Text color='quiet'>
          <FormattedMessage
            id='Preferences.panel.subTitle'
            defaultMessage='This is how we notify you about changes in your dashboard and timely wealth-building tips'
          />
        </Text>
      </div>
      <SubSection icon={EnvelopeHB}>
        <Text size='l'>
          <FormattedMessage
            id='Preferences.panel.sub.emails.title'
            defaultMessage='Emails'
          />
        </Text>
        <div className={styles.inline}>
          <Text color='quiet' element='span'>
            {clientEmail}
          </Text>
          <Button
            className={styles.editButton}
            minimal
            iconStart={IconPencil}
            onClick={navToEmailUpdates}
          />
        </div>
        <div className={styles.sliders}>
          <SliderSection
            name='Home Reports Toggle'
            defaultChecked={homeReportsActive}
            handleToggle={handleHomeReportsToggle}
            message={checkMessages.homeReports}
          />
          {!!isBuyer && (
            <SliderSection
              name='Buyer Reports Toggle'
              defaultChecked={buyerReportsActive}
              handleToggle={handleBuyerReportsToggle}
              message={checkMessages.buyerReports}
            />
          )}
          {!!isNotificationsImplemented && (
            <SliderSection
              name='Notifications Toggle'
              defaultChecked={notificationsActive}
              handleToggle={handleNotificationsToggle}
              message={checkMessages.notifications}
            />
          )}
        </div>
        <If condition={isExplainerImplemented}>
          <Button
            className={styles.explainButton}
            onClick={handleShowExplainer}
          >
            <FormattedMessage
              id='Preferences.panel.sub.explainer.email'
              defaultMessage='What are these updates?'
            />
          </Button>
        </If>
      </SubSection>
      <If condition={renderSmsOptIn}>
        <SubSection icon={MobileHB}>
          <Text size='l'>
            <FormattedMessage
              id='Preferences.panel.sub.mobile.title'
              defaultMessage='Text Messages'
            />
          </Text>
          <If condition={clientMobile}>
            <div className={styles.inline}>
              <Text color='quiet' element='span'>
                <Format.Phone value={clientMobile} />
              </Text>
              <Button
                className={styles.editButton}
                minimal
                iconStart={IconPencil}
                onClick={() => navToTextUpdates(TEXT_UPDATE_PAGES.UPDATE)}
              />
            </div>
          </If>
          <If condition={hasSubbed}>
            <div className={styles.sliders}>
              <SliderSection
                name='Home Reports SMS Toggle'
                defaultChecked={homeReportsSmsActive}
                handleToggle={handleHomeReportsSMSToggle}
                message={checkMessages.homeReports}
              />
            </div>
          </If>
          <Unless condition={hasSubbed}>
            <Button
              className={styles.smsButton}
              onClick={() => navToTextUpdates(TEXT_UPDATE_PAGES.OPT_IN)}
            >
              <FormattedMessage
                id='Preferences.panel.sub.explainer.sms'
                defaultMessage='Receive digests via text'
              />
            </Button>
          </Unless>
        </SubSection>
      </If>
    </div>
  )
}

export const SubSection = ({ icon: Icon, children }) => (
  <div className={styles.subSection}>
    <div className={styles.iconWrap}>
      <Icon />
    </div>
    {children}
  </div>
)
SubSection.propTypes = {
  icon: PropTypes.any,
  children: PropTypes.any
}

const SliderSection = ({ name, defaultChecked, handleToggle, message }) => {
  const intlFormat = useFormatMessage()

  return (
    <CheckBox.Slider
      className={styles.sliderButton}
      name={name}
      defaultChecked={defaultChecked}
      onChange={handleToggle}
    >
      <Text className={styles.sliderText} size='l' color='quiet' element='span'>
        {intlFormat(message)}
      </Text>
    </CheckBox.Slider>
  )
}
SliderSection.propTypes = {
  name: PropTypes.string,
  defaultChecked: PropTypes.bool,
  handleToggle: PropTypes.func,
  message: PropTypes.shape({
    id: PropTypes.string,
    defaultMessage: PropTypes.string
  })
}

const checkMessages = defineMessages({
  homeReports: {
    id: 'Preferences.panel.check.homeReports',
    defaultMessage: 'Home value updates'
  },
  buyerReports: {
    id: 'Preferences.panel.check.buyerReports',
    defaultMessage: 'Your buyer reports'
  },
  notifications: {
    id: 'Preferences.panel.check.notifications',
    defaultMessage: 'Important notifictions'
  }
})

export default PreferencesPanel
